import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, type SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, type Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { type DataSourceStatus, DataSourceStatusCode } from '../../../data-viz/data-source.interface';
import { CardShellComponent } from '../../../data-viz/data-viz-widgets/shells/card/card-shell';
import type { ScaleRange, WidgetScaleConfig } from '../../../providers/scales/scales.interface';
import { ScalesProvider } from '../../../providers/scales/scales.provider';
import { ScaleComponent, type ScaleLabels } from '../../scale/scale.component';
import { AbstractTailoredBasicWidget } from '../AbstractTailoredBasicWidget.class';
import type { ScaleCode } from './../../../providers/scales/scales.interface';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./scale-widget.scss'],
    selector: 'scale-widget',
    templateUrl: 'scale-widget.html',
    imports: [CardShellComponent, ScaleComponent, AsyncPipe, TranslatePipe]
})
export class ScaleWidgetComponent extends AbstractTailoredBasicWidget<{ scaleCode: ScaleCode }> {
    private _scalesProvider = inject(ScalesProvider);

    private _observableInputsManager = new ObservableInputsManager<ScaleWidgetComponent>(this);
    context$ = this._observableInputsManager.observe('context');
    config$ = this._observableInputsManager.observe('config');

    value$!: Observable<number>;
    ranges$!: Observable<ScaleRange[]>;

    widgetConfig?: WidgetScaleConfig;
    labels?: ScaleLabels;

    statusRanges$: BehaviorSubject<DataSourceStatus> = new BehaviorSubject<DataSourceStatus>({ code: DataSourceStatusCode.PENDING });
    statusValue$: BehaviorSubject<DataSourceStatus> = new BehaviorSubject<DataSourceStatus>({ code: DataSourceStatusCode.PENDING });
    status$!: Observable<DataSourceStatus>;

    ngOnInit() {
        this.config$
            .pipe(
                filter(isNotNullish),
                map(config => config.scaleCode),
                untilDestroyed(this)
            )
            .subscribe(scaleCode => {
                this.widgetConfig = this._scalesProvider.getScaleLabels(scaleCode);
                this.labels = this._scalesProvider.getScaleLabels(scaleCode);
            });

        this.value$ = combineLatest([this.context$, this.config$]).pipe(
            tap(() => {
                this.statusValue$.next({
                    code: DataSourceStatusCode.PENDING
                });
            }),
            switchMap(([context, config]) =>
                this._scalesProvider.getScaleValue$(config.scaleCode, context.currentPeriodStart, context.currentPeriodEnd, context.nodeId)
            ),
            tap(() => {
                this.statusValue$.next({
                    code: DataSourceStatusCode.OK
                });
            })
        );

        this.ranges$ = combineLatest([this.context$, this.config$]).pipe(
            tap(() => {
                this.statusRanges$.next({
                    code: DataSourceStatusCode.PENDING
                });
            }),
            switchMap(([context, config]) => this._scalesProvider.getScaleRanges$(config.scaleCode, context.nodeId)),
            tap(ranges => {
                this.statusRanges$.next(
                    ranges && ranges.length > 0
                        ? {
                              code: DataSourceStatusCode.OK
                          }
                        : {
                              code: DataSourceStatusCode.NO_DATA
                          }
                );
            })
        );

        this.status$ = combineLatest([this.statusValue$, this.statusRanges$]).pipe(
            map(([statusValue, statusRange]) => {
                return statusValue.code === DataSourceStatusCode.PENDING
                    ? {
                          code: DataSourceStatusCode.PENDING
                      }
                    : statusRange;
            })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }
}
